import React, { useState } from 'react';
import CourseApprovalMenu from './approval/CourseApprovalMenu';
import CourseApprovalMessage from './approval/CourseApprovalMessage';
import firebase from 'firebase/app';
import 'firebase/firestore';
import { AnnouncementData } from '@ypp-portal/models/announcement/announcementData';
import { mapFirestoreError } from '@util/mapFirebaseError';
import { AnnouncementDataConverter } from '@data/announcements/AnnouncementDataConverter';
import { ApprovalStatus, ApprovalInfo } from '@data/course/approval/approval';

interface GenericReviewBoxProps {
  approvalStatus: ApprovalStatus;
  approvalRef: firebase.firestore.DocumentReference<ApprovalInfo>;
  createAnnouncement: () => AnnouncementData;
  renderInfo: React.FC;
}

const announcementConverter = new AnnouncementDataConverter();

const GenericReviewBox: React.FC<GenericReviewBoxProps> = ({
  approvalStatus,
  approvalRef,
  createAnnouncement,
  renderInfo,
}) => {
  const [submitError, setSubmitError] = useState('');

  const sendAnnouncement = async () => {
    const announcement = createAnnouncement();
    await firebase
      .firestore()
      .collection('announcements')
      .doc()
      .withConverter(announcementConverter)
      .set(announcement);
  };

  const updateStatus = async (status: ApprovalStatus, comments: string) => {
    let data: Partial<ApprovalInfo>;

    // Don't include empty comments
    if (comments.trim() === '') {
      data = { status };
    } else {
      data = { status, comments };
    }

    // Don't do in parallel because we don't want to send an announcement if the update fails
    await approvalRef.update(data).catch((e: firebase.firestore.FirestoreError) => {
      setSubmitError(
        'Something went wrong updating the status of this course: ' + mapFirestoreError(e)
      );
    });

    await sendAnnouncement().catch((e: firebase.firestore.FirestoreError) => {
      setSubmitError(
        'The status for this course was updated correctly, but something went wrong ' +
          'sending an announcement to the instructor: ' +
          mapFirestoreError(e)
      );
    });

    setSubmitError('');
  };

  return (
    <div className='bg-cultured mx-8 my-6 p-6 flex flex-col lg:flex-row items-center'>
      <div className='flex-grow border-2 border-black p-6'>{renderInfo({})}</div>
      <div className='mt-8 md:ml-8 md:w-1/4'>
        {approvalStatus === ApprovalStatus.NoReview ? (
          <CourseApprovalMenu onSubmit={updateStatus} />
        ) : (
          <CourseApprovalMessage approvalStatus={approvalStatus} />
        )}
        <p className='text-red-600'>{submitError}</p>
      </div>
    </div>
  );
};

export default GenericReviewBox;
